<template>
  <div class="calendar-wrapper">
    <div
      v-if="loading"
    >
      <div class="loading-spinner">
        <div class="dot" />
        <div class="dot" />
        <div class="dot" />
      </div>
    </div>
    <div v-show="!loading">
      <!-- search bar-->
      <div class="search-filters">
        <b-form-group :label="$t('titlename')">
          <b-form-input
            v-model="searchTerm"
            class="input-box"
            :placeholder="$t('searchtitlename')"
          />
        </b-form-group>

        <div>
          <b-form-group :label="$t('searchdevice')">
            <div
              v-if="isLoadingDevice"
              class="spinner-container"
            >
              <b-spinner label="Loading..." />
            </div>
            <b-form-select
              v-if="formattedOptions.length > 0"
              v-model="event.deviceName"
              :select-size="3"
              required
              class="input-box"
              @change="onDeviceChange"
            >
              <b-form-select-option
                v-for="(option, index) in formattedOptions"
                :key="`${option.value}-${index}`"
                :value="option.text"
                :class="{
                  'category-option': option.isCategory,
                  'subcategory-option': option.isSubcategory,
                  'device-option': option.isDevice
                }"
                :disabled="option.isCategory || option.isSubcategory"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </div>

        <b-button
          style="margin-top: 12px; background-color: #02a63e; color: white; border: none;"
          variant="text-warning"
          @click="SearchEvents"
        >
          <b-icon
            icon="search"
            aria-hidden="true"
          />
          {{ $t('search') }}
        </b-button>

        <b-button
          style="margin-top: 12px; height: 40px; background-color: gray; color: white; border: none;"
          variant="text-warning"
          @click="clearSearch"
        >
          {{ $t('reset') }}
        </b-button>
      </div>

      <div class="calendar-container">
        <div
          ref="calendarRef"
          class="calendar-view"
        />
        <b-modal
          v-model="noResults"
          :title="$t('nodata')"
          hide-footer
        >
          <p>{{ $t('notfoundlist') }}</p>
        </b-modal>

        <!-- Event Modal -->
        <EventModal
          v-if="isModalVisible"
          :date="selectedDate"
          :event="selectedEvent"
          @close="closeModal"
          @save="saveEvent"
        />

        <!-- Event Tooltip -->
        <div
          v-if="!isMobile && tooltip"
          class="event-tooltip"
          :style="tooltipStyle"
        >
          <h4><strong>{{ tooltip.title || this.$t('nodetails') }}</strong></h4>
          <p><strong>{{ $t('start') }}:</strong> {{ tooltip.start || this.$t('nodetails') }}</p>
          <p><strong>{{ $t('end') }}:</strong> {{ tooltip.end || this.$t('nodetails') }}</p>
          <p><strong>{{ $t('devicename') }}:</strong> {{ tooltip.deviceName || this.$t('nodetails') }}</p>
          <p><strong>{{ $t('details') }}:</strong> {{ tooltip.description || this.$t('nodetails') }}</p>
        </div>

        <!-- Delete Confirmation Modal -->
        <b-modal
          v-model="isDeleteModalVisible"
          :title="$t('deleteconfirmEvent')"
          :ok-title="$t('confirm')"
          :cancel-title="$t('cancel')"
          @ok="deleteEvent"
        >
          <div v-if="selectedEvent">
            <h5><strong>{{ $t('title') }}:</strong> {{ selectedEvent.title }}</h5>
            <h5><strong>{{ $t('devicename') }}:</strong> {{ selectedEvent.deviceName || this.$t('nodetails') }}</h5>
            <h5><strong>{{ $t('details') }}:</strong> {{ selectedEvent.description || this.$t('nodetails') }}</h5>
            <h5><strong>{{ $t('start') }}:</strong> {{ formatDateTime(selectedEvent.startDateTime) || this.$t('nodetails') }}</h5>
            <h5><strong>{{ $t('end') }}:</strong> {{ formatDateTime(selectedEvent.endDateTime) || this.$t('nodetails') }}</h5>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import jaLocale from '@fullcalendar/core/locales/ja'
import thLocale from '@fullcalendar/core/locales/th'
import apiAuth from '@/services/auth'
import apiMst from '@/services/masterDate'
import EventModal from './EventModal.vue'

export default {
  name: 'CalendarComponent',
  components: {
    EventModal,
  },
  data() {
    return {
      calendar: null,
      event: {
        title: '',
        description: '',
        startDateTime: this.formatDateTime(this.date),
        endDateTime: this.formatDateTime(this.date),
        deviceCategoryId: null,
        deviceSubcategoryId: null,
        deviceId: null,
      },
      isModalVisible: false,
      isDeleteModalVisible: false,
      selectedEvent: null,
      selectedDate: null,
      tooltip: null,
      tooltipStyle: {},
      loading: false,
      searchTerm: '',
      formattedOptions: [],
      noResults: false,
      devicesOptions: [],
      deviceCategoriesOptions: [],
      deviceSubcategoriesOptions: [],
      isLoadingDevice: false,
      isLoadingDeviceCate: false,
      isMobile: false,

    }
  },
  mounted() {
    this.$nextTick(() => {
      Promise.all([
        this.fetchEvents(),
        this.fetchCategories(),
        this.fetchDevices(),
      ])
      const savedLanguage = localStorage.getItem('language') || 'en'
      this.$i18n.locale = savedLanguage
      this.initCalendar()
      setInterval(this.updateCalendar, 24 * 60 * 60 * 1000)
      this.$store.dispatch('initializeStore')
    })
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    if (this.calendar) {
      this.calendar.destroy()
    }
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 720
    },

    formatDateTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('th-TH', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },

    initCalendar() {
      const calendarEl = this.$refs.calendarRef
      let { locale } = this.$i18n

      if (locale === 'jp') {
        locale = 'ja'
      }

      let fullCalendarLocale = 'en'

      if (locale === 'th') {
        fullCalendarLocale = thLocale
      } else if (locale === 'ja') {
        fullCalendarLocale = jaLocale
      }

      this.calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        events: this.events,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        locale: fullCalendarLocale,
        timeZone: 'local',
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
        eventClick: this.handleEventClick,
        dateClick: this.handleDateClick,
        eventMouseEnter: this.handleEventMouseEnter,
        eventMouseLeave: this.handleEventMouseLeave,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
      })

      this.calendar.render()
    },

    async fetchEvents() {
      this.loading = true
      this.noResults = false
      try {
        const startDate = new Date()
        startDate.setFullYear(startDate.getFullYear() - 1)
        const endDate = new Date()
        endDate.setFullYear(endDate.getFullYear() + 1)

        const start = startDate.toISOString().slice(0, 16)
        const end = endDate.toISOString().slice(0, 16)

        const response = await apiAuth.get(`/calendar/getdata?start=${start}&end=${end}`)

        if (response.data && Array.isArray(response.data)) {
          this.events = response.data.map(event => ({
            id: event.id,
            title: event.title,
            start: event.timestart,
            end: event.timeend,
            display: 'block',
            backgroundColor: '#3788d8',
            borderColor: '#2c6cb0',
            textColor: '#ffffff',
            extendedProps: {
              description: event.description,
              device: event.device.id,
              deviceName: event.device.deviceName,
            },
          }))

          if (this.calendar) {
            this.calendar.removeAllEvents()
            this.calendar.addEventSource(this.events)
          }
        }
      } catch (error) {
        console.error('Error fetching events:', error)
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.calendar) {
            this.calendar.render()
          }
        })
      }
    },

    async SearchEvents() {
      this.isLoading = true
      this.noResults = false
      try {
        const searchTerm = this.searchTerm || ''
        const deviceName = this.event.deviceName || ''

        console.log(`Fetching events searchTerm: ${searchTerm} and deviceName: ${deviceName}`)

        const response = await apiAuth.get(
          `/calendar/getdata/search?searchTerm=${searchTerm}&deviceName=${deviceName}`,
        )

        if (response.data && response.data.length > 0) {
          this.events = response.data.map(event => ({
            id: event.id,
            title: event.title,
            start: event.timestart,
            end: event.timeend,
            display: 'block',
            backgroundColor: '#3788d8',
            borderColor: '#2c6cb0',
            textColor: '#ffffff',
            extendedProps: {
              description: event.description,
              deviceName: event.device.deviceName,
            },
          }))

          if (this.calendar) {
            this.calendar.removeAllEvents()
            this.calendar.addEventSource(this.events)
          }
        } else {
          this.noResults = true
        }
      } catch (error) {
        console.error('Error fetching events:', error)
      } finally {
        this.isLoading = false
      }
    },

    handleEventClick(info) {
      this.selectedEvent = {
        id: info.event.id,
        title: info.event.title,
        description: info.event.extendedProps.description,
        deviceName: info.event.extendedProps.deviceName,
        startDateTime: info.event.start,
        endDateTime: info.event.end,
      }
      this.isDeleteModalVisible = true
    },

    handleDateClick(info) {
      this.selectedDate = info.dateStr
      this.selectedEvent = null
      this.isModalVisible = true
    },

    handleEventMouseEnter(info) {
      if (this.isMobile) {
        return
      }

      const rect = info.el.getBoundingClientRect()
      const tooltipWidth = 300
      const tooltipHeight = 150

      this.tooltip = {
        title: info.event.title,
        start: this.formatDateTime(info.event.start),
        end: this.formatDateTime(info.event.end),
        deviceName: info.event.extendedProps.deviceName,
        description: info.event.extendedProps.description,
      }

      this.tooltipStyle = this.calculateTooltipPosition(rect, tooltipWidth, tooltipHeight)
    },

    calculateTooltipPosition(rect, tooltipWidth, tooltipHeight) {
      let left = rect.left + rect.width / 2 - tooltipWidth / 2
      let top = rect.top - tooltipHeight - 10

      left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10))
      if (top < 0) {
        top = rect.bottom + 10
      }

      return {
        left: `${left}px`,
        top: `${top}px`,
        position: 'fixed',
        zIndex: 2000,
        pointerEvents: 'none',
      }
    },

    handleEventMouseLeave() {
      if (this.isMobile) {
        return
      }
      this.tooltip = null
    },

    async deleteEvent() {
      if (!this.selectedEvent?.id) return

      this.loading = true
      try {
        await apiAuth.delete(`/calendar/deletedata/${this.selectedEvent.id}`)
        await this.fetchEvents()
        this.isDeleteModalVisible = false
        this.selectedEvent = null
      } catch (error) {
        console.error('Error deleting event:', error)
      } finally {
        this.loading = false
      }
    },

    closeModal() {
      this.isModalVisible = false
      this.selectedEvent = null
    },

    async saveEvent(eventData) {
      if (!eventData?.title || !eventData?.startDateTime || !eventData?.endDateTime || !eventData?.deviceId) {
        console.error('Missing required event data')
        return
      }

      this.loading = true
      try {
        const payload = {
          title: eventData.title,
          description: eventData.description,
          timestart: new Date(eventData.startDateTime).toISOString(),
          timeend: new Date(eventData.endDateTime).toISOString(),
          device: {
            id: eventData.deviceId,
          },
        }

        await apiAuth.post('/calendar/postdata', payload)
        await this.fetchEvents()
        this.closeModal()
      } catch (error) {
        console.error('Error saving event:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchCategories() {
      this.isLoading = true
      try {
        const response = await apiMst.get('/device')
        this.deviceCategoriesOptions = response.data.map(category => ({
          value: category.id,
          text: category.name,
          subcate: category.deviceSubCate || [],
        }))
      } catch (error) {
        console.error('Error fetching device categories:', error)
        this.deviceCategoriesOptions = []
      } finally {
        this.isLoading = false
      }
    },

    async fetchSubcategories() {
      if (!this.event.deviceCategoryId) return
      this.isLoadingDeviceCate = true
      try {
        const response = await apiMst.get(`/subdevice/${this.event.deviceCategoryId}`)

        if (Array.isArray(response.data) && response.data.length > 0) {
          this.deviceSubcategoriesOptions = response.data.map(subcat => ({
            value: subcat.id,
            text: subcat.name,
          }))
        } else {
          this.deviceSubcategoriesOptions = []
          console.warn('ไม่พบหมวดหมู่ย่อยของอุปกรณ์')

          this.$bvToast.toast(this.$t('notfoundsubcateIndevice'), {
            title: this.$t('warning'),
            variant: 'warning',
            solid: true,
          })
        }
      } catch (error) {
        console.error('Error fetching device subcategories:', error)
        this.deviceSubcategoriesOptions = []

        this.$bvToast.toast(this.$t('getsubcatedevicefailed'), {
          title: this.$t('error'),
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoadingDeviceCate = false
      }
    },

    async fetchDevices() {
      this.isLoadingDevice = true
      try {
        const response = await apiMst.get('/deviceListAll')
        if (response.data) {
          const deviceData = response.data
          this.devicesOptions = deviceData.map(device => ({
            value: device.id,
            text: device.deviceName || device.name,
            categoryId: device.categoryId || device.category?.id || device.deviceCategoryId,
            subcategoryId: device.subcategoryId || device.subcategory?.id || device.deviceSubcategoryId,
          }))
          this.formatOptions()
        } else {
          console.warn('No devices found or unexpected response format')
          this.devicesOptions = []
        }
      } catch (error) {
        console.error('Error fetching devices:', error)
        this.devicesOptions = []
      } finally {
        this.isLoadingDevice = false
      }
    },

    formatOptions() {
      this.formattedOptions = []
      if (this.deviceCategoriesOptions.length === 0 || this.devicesOptions.length === 0) {
        return
      }

      this.deviceCategoriesOptions.forEach(category => {
        this.formattedOptions.push({
          value: category.value,
          text: category.text,
          isCategory: true,
        })

        if (category.subcate && category.subcate.length > 0) {
          const sortedSubcategories = category.subcate.sort((a, b) => a.name.localeCompare(b.name))

          sortedSubcategories.forEach(subcategory => {
            this.formattedOptions.push({
              value: subcategory.id,
              text: `${subcategory.name}`,
              isSubcategory: true,
              categoryId: category.value,
            })

            const subcategoryDevices = this.devicesOptions.filter(device => device.subcategoryId === subcategory.id)
            const sortedDevices = subcategoryDevices.sort((a, b) => a.text.localeCompare(b.text))

            sortedDevices.forEach(device => {
              this.formattedOptions.push({
                value: device.value,
                text: `${device.text}`,
                isDevice: true,
                categoryId: device.categoryId,
                subcategoryId: device.subcategoryId,
              })
            })
          })
        }
      })

      this.formattedOptions.sort((a, b) => {
        if (a.isCategory && b.isCategory) {
          return a.text.localeCompare(b.text)
        } if (a.isSubcategory && b.isSubcategory) {
          return a.text.localeCompare(b.text)
        } if (a.isDevice && b.isDevice) {
          return a.text.localeCompare(b.text)
        }
        return 0
      })

      console.log('Formatted Options:', this.formattedOptions)
    },

    onDeviceChange() {
      const selectedOption = this.formattedOptions.find(
        option => option.text === this.event.deviceId && option.isDevice,
      )

      if (selectedOption) {
        this.event.deviceId = selectedOption.text
        this.event.deviceId = selectedOption.value
        this.event.deviceSubcategoryId = selectedOption.subcategoryId
        this.event.deviceCategoryId = selectedOption.categoryId
      }
    },

    clearSearch() {
      this.searchTerm = ''
      this.event.deviceName = ''
      this.event.categoryId = null
    },

  },
}
</script>

<style scoped>
.calendar-wrapper {
  position: relative;
  width: 100%;
  height: auto;
}

.calendar-container {
  height: 800px;
  margin: 20px;
}

.calendar-view {
  height: 100%;
  background-color: white;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

}

.search-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.event-tooltip {
  position: fixed;
  background: white;
  color: black;
  padding: 10px;
  border-radius: 5px;
  font-size: 14px;
  max-width: 300px;
  border: 1px solid #ccc;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.input-box{
  width: 180px;
  height: 35px;
}

.category-option {
  background-color: #949494;
  color: white;
  font-weight: bold;
  font-size: 16px;
}
.subcategory-option{
  font-weight: bold;
  font-size: 14px;
}

.spinner-container {
  margin-left: 25px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
.dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: #5496ec;
  animation: bounce 0.6s infinite alternate;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-20px);
  }
}

/* Media Queries สำหรับมือถือ */
@media (max-width: 768px) {
  .calendar-container {
    margin: 10px;
  }

  .calendar-view {
    padding: 5px;
  }

  .event-tooltip {
    max-width: 80%;
    font-size: 12px;
  }
}

</style>
